import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * PhotoImport — photograph a real whiteboard, square it up, put it on the
 * board (docs/WHITEBOARD-PHOTO-IMPORT-ROADMAP.md, P0).
 *
 * P0 reads nothing. It takes a photo, lets you drag its four corners onto
 * the board's corners, warps it straight, and lands it as a faint backdrop
 * you can draw on top of by hand. That is useful on its own and carries no
 * model risk — and it is the layer every later slice needs, because an
 * imported sticky is only trustworthy if you can see the photograph
 * underneath it.
 *
 * The corners are DRAGGED, never detected. Auto-detecting a whiteboard's
 * outline fails quietly (white board, white wall, no frame, a person in
 * front of it) and a silently sheared import is worse than no import.
 *
 * The warp happens HERE, in the browser, so what you approve is exactly
 * what gets uploaded — the server never has to be trusted to reproduce it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Image as ImageIcon, Loader2, RotateCcw, X } from 'lucide-react';
import { defaultQuad, quadIsSane, rectSizeFor, warpToRect } from '../../canvas/warp.js';
/**
 * Rectified pixel cap. This is the resolution the sticky read (P1) gets to
 * work with, so it is set by what handwriting needs, not by what looks fine
 * as a backdrop: a wall of thirty stickies across 3200px leaves each note
 * ~400px wide, which is readable. At 2200 it is ~250px, which is guessing.
 * A 3200px JPEG at q0.9 is a couple of megabytes, well inside the 8MB cap.
 */
const MAX_UPLOAD_EDGE = 3200;
export function PhotoImport({ onImport, onClose }) {
    const [img, setImg] = useState(null);
    const [quad, setQuad] = useState([]);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [box, setBox] = useState({ w: 0, h: 0, scale: 1 });
    const stageRef = useRef(null);
    const dragRef = useRef(null);
    /** The object URL stays alive while the photo is on screen. Revoking it
     *  on load kills the <img> that displays it — the decoded bitmap survives
     *  for canvas work, so the warp still runs and only the PREVIEW goes
     *  blank, which is exactly the kind of bug you only see by looking. */
    const urlRef = useRef(null);
    const dropUrl = useCallback(() => {
        if (urlRef.current) {
            URL.revokeObjectURL(urlRef.current);
            urlRef.current = null;
        }
    }, []);
    useEffect(() => dropUrl, [dropUrl]);
    const takeFile = useCallback((file) => {
        if (!file)
            return;
        setError(null);
        dropUrl();
        const url = URL.createObjectURL(file);
        urlRef.current = url;
        const el = new Image();
        el.onload = () => {
            setImg(el);
            setQuad(defaultQuad(el.naturalWidth, el.naturalHeight));
        };
        el.onerror = () => {
            dropUrl();
            // Overwhelmingly this is an iPhone HEIC, which most browsers cannot
            // decode and Claude cannot read either. Say so, plainly.
            setError(/heic|heif/i.test(file.type) || /\.hei[cf]$/i.test(file.name)
                ? 'That looks like an iPhone HEIC photo, which browsers cannot open. In the Photos app, share it as JPEG, or set Camera → Formats → Most Compatible.'
                : 'That file could not be opened as an image. JPEG, PNG or WebP.');
        };
        el.src = url;
    }, [dropUrl]);
    // Fit the photo into the stage; the quad stays in IMAGE pixels and only
    // the handles are drawn in display space, so a resize never moves it.
    useEffect(() => {
        if (!img)
            return undefined;
        const fit = () => {
            const el = stageRef.current;
            if (!el)
                return;
            const avail = el.getBoundingClientRect();
            const scale = Math.min(avail.width / img.naturalWidth, avail.height / img.naturalHeight, 1);
            setBox({ w: img.naturalWidth * scale, h: img.naturalHeight * scale, scale });
        };
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, [img]);
    const moveCorner = (e) => {
        const i = dragRef.current;
        if (i == null || !img)
            return;
        const el = stageRef.current?.querySelector('.tpi-frame');
        if (!el)
            return;
        const r = el.getBoundingClientRect();
        const x = Math.max(0, Math.min(img.naturalWidth, (e.clientX - r.left) / box.scale));
        const y = Math.max(0, Math.min(img.naturalHeight, (e.clientY - r.top) / box.scale));
        setQuad((q) => q.map((p, k) => (k === i ? { x, y } : p)));
    };
    const sane = img != null && quadIsSane(quad);
    const confirm = async () => {
        if (!img || !sane || busy)
            return;
        setBusy(true);
        setError(null);
        try {
            // Read the photo at full size…
            const read = document.createElement('canvas');
            read.width = img.naturalWidth;
            read.height = img.naturalHeight;
            const rctx = read.getContext('2d', { willReadFrequently: true });
            if (!rctx)
                throw new Error('canvas unavailable');
            rctx.drawImage(img, 0, 0);
            const src = rctx.getImageData(0, 0, read.width, read.height);
            // …square it up…
            const { w, h } = rectSizeFor(quad, MAX_UPLOAD_EDGE);
            const warped = warpToRect({ data: src.data, width: src.width, height: src.height }, quad, w, h);
            if (!warped)
                throw new Error('those corners do not make a usable shape');
            // …and hand over exactly what was approved.
            // The warped pixels go onto their own canvas first. putImageData
            // REPLACES pixels including alpha, so painting it straight over a
            // white fill would punch transparent holes wherever the quad reached
            // past the photo — and a JPEG turns those black. Compositing with
            // drawImage lets the white show through instead.
            const layer = document.createElement('canvas');
            layer.width = w;
            layer.height = h;
            const lctx = layer.getContext('2d');
            if (!lctx)
                throw new Error('canvas unavailable');
            const id = lctx.createImageData(w, h);
            id.data.set(warped.data);
            lctx.putImageData(id, 0, 0);
            const out = document.createElement('canvas');
            out.width = w;
            out.height = h;
            const octx = out.getContext('2d');
            if (!octx)
                throw new Error('canvas unavailable');
            octx.fillStyle = '#ffffff';
            octx.fillRect(0, 0, w, h);
            octx.drawImage(layer, 0, 0);
            const blob = await new Promise((res) => out.toBlob(res, 'image/jpeg', 0.9));
            if (!blob)
                throw new Error('the photo could not be encoded');
            const buf = await blob.arrayBuffer();
            let bin = '';
            const bytes = new Uint8Array(buf);
            for (let i = 0; i < bytes.length; i += 0x8000) {
                bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
            }
            await onImport({
                data_base64: btoa(bin),
                content_type: 'image/jpeg',
                px_w: w,
                px_h: h,
            });
            onClose();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'That photo could not be added.');
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsx("div", { className: "tpi", role: "dialog", "aria-label": "Add a whiteboard photo", children: _jsxs("div", { className: "tpi-panel", children: [_jsxs("div", { className: "tpi-head", children: [_jsx(ImageIcon, { size: 14 }), _jsx("span", { className: "tpi-title", children: "Add a whiteboard photo" }), _jsx("button", { type: "button", className: "tgo-mini", title: "Close", onClick: onClose, children: _jsx(X, { size: 12 }) })] }), !img ? (_jsxs("label", { className: "tpi-drop", children: [_jsx("input", { type: "file", accept: "image/jpeg,image/png,image/webp", onChange: (e) => takeFile(e.target.files?.[0]) }), _jsx("strong", { children: "Choose a photo" }), _jsx("span", { children: "JPEG, PNG or WebP. You will square it up on the next step." })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "tpi-stage", ref: stageRef, children: _jsxs("div", { className: "tpi-frame", style: { width: box.w, height: box.h }, children: [_jsx("img", { src: img.src, alt: "", draggable: false }), _jsx("svg", { className: "tpi-outline", viewBox: `0 0 ${box.w} ${box.h}`, "aria-hidden": "true", children: _jsx("polygon", { points: quad.map((p) => `${p.x * box.scale},${p.y * box.scale}`).join(' '), className: sane ? '' : 'tpi-bad' }) }), quad.map((p, i) => (_jsx("span", { className: "tpi-corner", role: "presentation", style: { left: p.x * box.scale, top: p.y * box.scale }, onPointerDown: (e) => {
                                            dragRef.current = i;
                                            e.currentTarget.setPointerCapture(e.pointerId);
                                        }, onPointerMove: moveCorner, onPointerUp: () => { dragRef.current = null; }, onPointerCancel: () => { dragRef.current = null; } }, i)))] }) }), _jsx("p", { className: "tpi-hint", children: "Drag the four dots onto the corners of the whiteboard. Everything inside them gets straightened; everything outside is cropped away." })] })), error && _jsx("p", { className: "tpi-error", children: error }), _jsxs("div", { className: "tpi-foot", children: [img && (_jsxs("button", { type: "button", className: "tgo-mini tgo-mini-wide", title: "Start again with a different photo", onClick: () => { dropUrl(); setImg(null); setQuad([]); setError(null); }, children: [_jsx(RotateCcw, { size: 11 }), " Another photo"] })), _jsx("span", { className: "tpi-spacer" }), _jsxs("button", { type: "button", className: "tgo-prop-go", disabled: !sane || busy, onClick: confirm, children: [busy ? _jsx(Loader2, { size: 12, className: "tgl-spin" }) : _jsx(Check, { size: 12 }), "Straighten and add"] })] })] }) }));
}
export default PhotoImport;
